import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {
  AspectRatio,
  Box,
  Button,
  Grid,
  IconButton,
  Input,
  List,
  Option,
  Select,
  Sheet,
  Snackbar,
  Stack,
  Typography,
} from "@mui/joy";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroller";
import { Link, useParams, useSearchParams } from "react-router-dom";
import ListUnavailable from "../../components/Catalogue/ListUnavailable";
import { If, IfElse } from "../../components/Condition";
import { theme } from "../../components/Theme";
import { ItemService } from "../../services/item.service";
import { SupplierService } from "../../services/supplier.service";
import { getActiveBusiness, useAuthStore } from "../../store/session";
import { CartItemDataType } from "../../types/cart";
import { CatalogInventoryItemType } from "../../types/item";
import { getCartItems } from "../../utils/cart";
import ProductDetailsModal from "./components/ProductDetailsModal";
import useContainer from "./useContainer";
// import ProductListItem from "./components/ProductListItem";
import {
  ChevronDownIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import LanguageSelector from "../../components/LanguageSelector";
import CatalogueMetadata from "../../components/Catalogue/CatalogueMetadata";
import CatalogueHeader from "./components/CatalogueHeader";
import LoadingModal from "../../components/LoadingModal/LoadingModal";
import { getCountryInfo } from "../../utils/country";
import { formatDate } from "../../utils/formatDate";
import { CatalogType } from "../../types/catalog";
import { useMutation } from "@tanstack/react-query";
import { LockClosedIcon } from "@heroicons/react/24/solid";
import OTPInput from "react-otp-input";
import toast from "react-hot-toast";
import showErrorToast, { ErrorToastConfig } from "../../components/Error";
import { ProductCardBase } from "./components/ProductCardBase";
import LogoImg from "../../assets/linkz-logo-small.png";

interface CatalogAccess {
  catalogId: string;
  date: Date;
}

const sortOptions = [
  { value: "", label: "Sort By" },
  { value: "createdAt_asc", label: "Newest first" },
  { value: "createdAt_desc", label: "Latest first" },
  { value: "productName_asc", label: "A to Z (ascending)" },
  { value: "productName_desc", label: "Z to A (descending)" },
  // { value: "price_desc", label: "Highest Price" },
  // { value: "price_asc", label: "Lowest Price" },
];

const ListSupplierItems = () => {
  const {
    categories,
    setCategories,
    fetchAllItems,
    fetchCatalog,
    items,
    catalog,
    isLoading,
    setIsLoading,
    isItemLoading,
    pagination,
    unauthorized,
    setUnauthorized,
    hasMore,
    cartItems,
    setCartItems,
    cartUpdated,
    setCartUpdated,
    showToast,
    setShowToast,
    error,
    handleUpdateCart,
    handleUpdateOrder,
  } = useContainer();

  const { t } = useTranslation();

  const { catalogId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get("order");
  const query = searchParams.get("query");
  const sortBy = searchParams.get("sort_by");
  const sortDirection = searchParams.get("sort_direction") ?? "";
  const layoutType =
    searchParams.get("layout") ??
    catalog?.layoutItems?.toLocaleLowerCase() ??
    "list";
  const inventoryType = searchParams.get("inventory_type");

  const isGuestMode = window.location.pathname.startsWith("/public");
  const { session } = useAuthStore.getState();
  // const [viewMode, _setViewMode] = useState<string>("grid");
  const [toCart, _setToCart] = useState<CartItemDataType[]>([]);
  const [isMobile, setIsMobile] = useState(
    window.innerWidth < theme.breakpoints.values.sm
  );
  const [_subtotal, setSubtotal] = useState(0);
  const [selectedItem, setSelectedItem] = useState<{
    show: boolean;
    item: CatalogInventoryItemType;
  }>();

  useEffect(() => {
    if (window.innerWidth < theme.breakpoints.values.sm) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [window.innerWidth]);

  useEffect(() => {
    if (catalogId) {
      setIsLoading(true);
      // clear storedItemDetails
      if (session?.accessToken && isGuestMode) {
        window.location.href = `/client/suppliers/${catalogId}/items`;
        return;
      }
      fetchCatalog(catalogId, (res) => {
        if (res.catalogType !== "PUBLIC") {
          SupplierService.verifyAccess({
            catalogId: catalogId,
            businessId: getActiveBusiness()?.id ?? null,
            accountId: session?.account.id ?? null,
          })
            .then((res) => {
              if (!res.isValid) {
                const accessData = localStorage.getItem("catalog_access");
                if (!accessData) {
                  setUnauthorized(true);
                  return;
                }

                try {
                  const catalogAccess: CatalogAccess[] = JSON.parse(accessData);
                  const catalogEntry = catalogAccess.find(
                    (item) => item.catalogId === catalogId
                  );

                  if (!catalogEntry) {
                    setUnauthorized(true);
                    return;
                  }

                  // Convert stored date string back to Date object
                  const storedDate = new Date(catalogEntry.date);
                  const currentDate = new Date();
                  const sixHoursInMs = 6 * 60 * 60 * 1000;

                  if (
                    currentDate.getTime() - storedDate.getTime() <
                    sixHoursInMs
                  ) {
                    setUnauthorized(false);
                  } else {
                    setUnauthorized(true);
                    handleRemoveCatalogAccess(catalogId);
                  }
                } catch (error) {
                  console.error("Error checking catalog access:", error);
                  setUnauthorized(true);
                }
              } else {
                setUnauthorized(!res.isValid);
              }

              return fetchAllItems({
                catalogId: catalogId,
                paging: true,
                limit: 20,
                offset: 0,
                searchTerm: query,
                inventoryType: inventoryType !== "all" ? inventoryType : null,
                isReset: true,
                sortDirection: (["asc", "desc"].includes(sortDirection)
                  ? sortDirection
                  : "desc") as "asc" | "desc",
                sortBy: sortBy ? sortBy : "updatedAt",
              });
            })
            .then(() => {
              ItemService.getCatalogInventoryTypes({
                catalogId,
              }).then((res) => {
                setCategories(res);
              });
            })
            .catch((error) => {
              setUnauthorized(true);
              console.error(error);
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
          // initiateInventoryItems(catalogId, query ? query : undefined);
          // setIsLoading(true);
          fetchAllItems({
            catalogId: catalogId,
            paging: true,
            limit: 20,
            offset: 0,
            searchTerm: query,
            inventoryType: inventoryType !== "all" ? inventoryType : null,
            isReset: true,
            sortDirection: (["asc", "desc"].includes(sortDirection)
              ? sortDirection
              : "desc") as "asc" | "desc",
            sortBy: sortBy ? sortBy : "updatedAt",
          });
        }
      });
    }
  }, [catalogId, session, query, inventoryType, sortDirection, sortBy]);

  useEffect(() => {
    if (cartUpdated && catalogId) {
      setShowToast(true);

      setTimeout(() => {
        setShowToast(false);
      }, 3000);

      const tempCartItems = getCartItems(
        catalogId,
        session?.account.id || "wildcart",
        session?.accessToken ? "local" : "session"
      );

      if (tempCartItems) {
        setCartItems(tempCartItems);
      }

      setCartUpdated(false);
    }
  }, [cartUpdated]);

  useEffect(() => {
    if (toCart.length > 0) {
      let tmp = 0;
      toCart.forEach((c) => {
        tmp += (c.initPrice ?? 0) * c.quantity;
      });

      setSubtotal(tmp);
    } else {
      setSubtotal(0);
    }
  }, [toCart]);

  // memoize catalog's info, only recalculate if catalog changes
  const CATALOG_METADATA = useMemo(() => {
    if (!catalog) {
      return [];
    }

    return [
      {
        title: t("catalog.totalItems"),
        value: `${catalog.itemsCount} Items`,
      },
      {
        title: t("catalog.sellerEmail"),
        value: catalog.business?.companyEmail || "-",
      },
      {
        title: t("catalog.sellerLocation"),
        value: `${catalog.business?.companyAddress}, ${
          getCountryInfo(catalog.business?.companyCountry)?.name
        }`,
      },
      {
        title: t("catalog.catalogLastUpdated"),
        value: formatDate(new Date(catalog.updatedAt || "")),
      },
      {
        title: t("catalog.sellerPhone"),
        value: `+${catalog.business?.companyNumberCountryCode} ${catalog.business?.companyNumber}`,
      },
    ];
  }, [catalog]);

  const debouncedSetSearchTerm = useCallback(
    debounce((value: string) => {
      if (value === "") {
        searchParams.delete("query");
      } else {
        searchParams.set("query", value);
      }
      setSearchParams(searchParams);
    }, 300),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchTerm(event.target.value);
  };

  /**
   * take catalogId and remove from catalog_access object
   * @param catalogId
   * @returns
   */
  const handleRemoveCatalogAccess = (catalogId: string): void => {
    const existingData = localStorage.getItem("catalog_access");
    if (!existingData) return;

    const catalogAccess: CatalogAccess[] = JSON.parse(existingData);
    const updatedAccess = catalogAccess.filter(
      (item) => item.catalogId !== catalogId
    );

    localStorage.setItem("catalog_access", JSON.stringify(updatedAccess));
  };

  const orderDataCallback = () => {
    // setUnsavedOrderData(localStorage.getItem(`unsavedOrderData_${orderId}`));
  };

  // return (
  //   <InfiniteScroll
  //     pageStart={0}
  //     useWindow={false}
  //     loadMore={(_page: number) => {
  //       if (isLoading) {
  //         return;
  //       }

  //       console.log("PAGING CURRENT", pagination);

  //       fetchAllItems({
  //         catalogId: catalogId ?? "",
  //         paging: true,
  //         limit: 10,
  //         offset: 10 * pagination.current,
  //         searchTerm: query,
  //         inventoryType: inventoryType !== "all" ? inventoryType : null,
  //         isReset: false,
  //         sortDirection: (["asc", "desc"].includes(sortDirection)
  //           ? sortDirection
  //           : "desc") as "asc" | "desc",
  //         sortBy: sortBy ? sortBy : "updatedAt",
  //       });
  //     }}
  //     hasMore={hasMore}
  //     loader={
  //       isLoading ? (
  //         <div className="loader" key={0}>
  //           Loading...
  //         </div>
  //       ) : (
  //         <></>
  //       )
  //     }
  //     // getScrollParent={() => scrollableRef.current}
  //   >
  //     {items.length > 0 ? (
  //       <Box width={"100%"}>
  //         {layoutType === "grid" && (
  //           <Grid
  //             container
  //             columns={{
  //               xs: 12,
  //               lg: 60,
  //             }} // 12/2=6, 60/5=12
  //             spacing={{ xs: "8px", md: "24px" }}
  //             sx={{
  //               px: 0,
  //               // alignItems: "stretch",
  //               // gap: { xs: "8px", md: "24px" },
  //             }}
  //           >
  //             {items.map((i) => {
  //               return (
  //                 <Grid key={i.id} xs={6} lg={isGuestMode ? 10 : 12} xl={10}>
  //                   <ProductCardBase
  //                     item={i}
  //                     catalog={catalog}
  //                     mode="supplier"
  //                     handleUpdateCart={handleUpdateCart}
  //                     handleUpdateOrder={handleUpdateOrder}
  //                     setCartUpdated={setCartUpdated}
  //                   />
  //                 </Grid>

  //                 // <div key={i.id}>
  //                 //   <SupplierProductCard
  //                 //     item={i}
  //                 //     catalog={catalog}
  //                 //     orderDataCallback={
  //                 //       orderDataCallback
  //                 //     }
  //                 //     type="supplier"
  //                 //     displayType={"grid"}
  //                 //     onClick={() => {
  //                 //       setSelectedItem({
  //                 //         show: true,
  //                 //         item: i,
  //                 //       });
  //                 //     }}
  //                 //   />
  //                 // </div>
  //               );
  //             })}
  //           </Grid>
  //         )}
  //         {layoutType === "list" && (
  //           <List>
  //             {items.map((i) => {
  //               return (
  //                 <ProductCardBase
  //                   key={i.id}
  //                   item={i}
  //                   catalog={catalog}
  //                   displayType="list"
  //                   mode="supplier"
  //                   handleUpdateCart={handleUpdateCart}
  //                   handleUpdateOrder={handleUpdateOrder}
  //                   setCartUpdated={setCartUpdated}
  //                 />
  //                 // <div key={i.id}>
  //                 //   <SupplierProductCard
  //                 //     item={i}
  //                 //     catalog={catalog}
  //                 //     setCartUpdated={setCartUpdated}
  //                 //     orderDataCallback={
  //                 //       orderDataCallback
  //                 //     }
  //                 //     type="supplier"
  //                 //     displayType={"list"}
  //                 //     onClick={() => {
  //                 //       setSelectedItem({
  //                 //         show: true,
  //                 //         item: i,
  //                 //       });
  //                 //     }}
  //                 //   />
  //                 // </div>
  //               );
  //             })}
  //           </List>
  //         )}
  //       </Box>
  //     ) : (
  //       <div className="flex w-full mt-10">
  //         <p className="mx-auto my-auto text-base">
  //           {t("catalog.noItemInCatalog")}
  //         </p>
  //       </div>
  //     )}
  //   </InfiniteScroll>
  // );

  return (
    <Sheet
      className={"hide-scrollbar"}
      sx={{
        position: "relative",
        backgroundColor: "transparent",
        // overflowX: "hidden",
        overflowY: "auto",
        px: isGuestMode
          ? {
              xs: "16px",
              lg: "32px",
            }
          : { lg: "8px" },
        py: {
          xs: "16px",
          lg: isGuestMode ? "32px" : 0,
        },
        width: isGuestMode ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        gap: "32px",
      }}
    >
      <IfElse
        condition={!error}
        ifBlock={
          <IfElse
            condition={!unauthorized}
            ifBlock={
              <Stack
                direction={"column"}
                sx={{
                  gap: { xs: "8px", md: "24px" },
                  py: { xs: 0, lg: "32px" },
                }}
              >
                {/* guestmode header */}
                <If condition={isGuestMode}>
                  <Stack
                    direction={"row"}
                    sx={{
                      alignItems: "center",
                      width: "100%",
                      py: "16px",
                    }}
                  >
                    {/* logo */}
                    <Link to={"/"}>
                      <AspectRatio
                        ratio={1.09 / 0.5}
                        variant="plain"
                        sx={{
                          width: "109.05px",
                          height: "52px",
                          mr: "auto",
                        }}
                      >
                        <img src={LogoImg} alt="linkz-logo" />
                      </AspectRatio>
                    </Link>

                    <Box
                      sx={{
                        ml: "auto",
                        display: { xs: "block", lg: "none" },
                      }}
                    >
                      <LanguageSelector />
                    </Box>

                    <Stack
                      direction={"row"}
                      sx={{
                        display: { xs: "none", lg: "flex" },
                        gap: "8px",
                        ml: "auto",
                      }}
                    >
                      <LanguageSelector />

                      <Link
                        to={`/signin?callbackURL=/client/suppliers/${catalogId}/items`}
                        className="ml-3"
                      >
                        <Button
                          variant="primary-light"
                          className="min-w-[3rem]"
                        >
                          {t("landing.loginBtn")}
                        </Button>
                      </Link>
                    </Stack>
                  </Stack>
                </If>

                {/* pin input guestmode & catalogue items display */}
                {catalog && (
                  <>
                    {/* catalog details */}
                    <Stack
                      direction={"column"}
                      sx={{
                        gap: "24px",
                        py: { xs: "16px", lg: 0 },
                        width: "100%",
                      }}
                    >
                      {/* catalog header informations */}
                      <CatalogueHeader
                        catalog={catalog}
                        withCatalogType
                        withCart
                        cartItems={cartItems}
                      />
                      {/* catalog metadata informations */}
                      <CatalogueMetadata data={CATALOG_METADATA} />
                    </Stack>

                    {/* search bar */}
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      justifyContent={"space-between"}
                      gap={"24px"}
                      className="w-[100%]"
                    >
                      <Input
                        defaultValue={query ?? ""}
                        variant="linkz-input-search"
                        size="sm"
                        fullWidth={isGuestMode}
                        name="searchFormData.search"
                        placeholder={t("catalog.searchItemPlaceholder")}
                        startDecorator={<SearchOutlinedIcon />}
                        sx={{
                          flexGrow: 1,
                          width: { xs: "100%", lg: "41%" },
                        }}
                        onChange={handleChange}
                      />

                      <Stack
                        direction={{ xs: "column", lg: "row" }}
                        sx={{
                          gap: { xs: "1rem", lg: "0.5rem" },
                          justifyContent: "flex-end",
                          overflow: "auto",
                          width: "100%",
                          alignItems: "center",
                        }}
                        className="hide-scrollbar"
                      >
                        {/* category filter */}
                        <Select
                          slotProps={{
                            button: { sx: { whiteSpace: "nowrap" } },
                          }}
                          size="sm"
                          defaultValue={""}
                          sx={{
                            paddingY: "10px",
                            paddingX: "16px",
                            fontWeight: 600,
                            gap: "8px",
                            width: { xs: "100%", lg: "auto" },
                            color:
                              theme.colorSchemes.light.palette.others
                                .neutral_dark,
                            borderColor:
                              theme.colorSchemes.light.palette.others
                                .neutral_dark,
                          }}
                          indicator={<ChevronDownIcon width={18} height={18} />}
                          onChange={(
                            _event: React.SyntheticEvent | null,
                            value: string | null
                          ) => {
                            if (!value && value !== "") {
                              return;
                            }

                            if (value.length > 1) {
                              searchParams.set("inventory_type", value);
                            } else {
                              searchParams.delete("inventory_type");
                            }
                            setSearchParams(searchParams);
                          }}
                        >
                          <Option
                            color="primary"
                            value={""}
                            sx={{
                              color:
                                theme.colorSchemes.dark.palette.common.black,
                            }}
                          >
                            {t("catalog.supplier.items.allCategoryLabel")}
                          </Option>
                          {categories.map((cat) => {
                            return (
                              <Option
                                key={cat}
                                color="primary"
                                value={cat}
                                label={cat.toUpperCase()}
                                sx={{
                                  color:
                                    theme.colorSchemes.dark.palette.common
                                      .black,
                                }}
                              >
                                {cat}
                              </Option>
                            );
                          })}
                        </Select>

                        <Stack
                          direction={"row"}
                          gap={"0.5rem"}
                          width={{ xs: "100%", lg: "auto" }}
                        >
                          {/* sort by */}
                          <Select
                            slotProps={{
                              button: { sx: { whiteSpace: "nowrap" } },
                            }}
                            size="sm"
                            sx={{
                              paddingY: "10px",
                              paddingX: "16px",
                              fontWeight: 600,
                              gap: "8px",
                              width: { xs: "80%", lg: "auto" },
                              color:
                                theme.colorSchemes.light.palette.others
                                  .neutral_dark,
                              borderColor:
                                theme.colorSchemes.light.palette.others
                                  .neutral_dark,
                            }}
                            indicator={
                              <ChevronDownIcon width={18} height={18} />
                            }
                            defaultValue={
                              sortBy && sortDirection
                                ? sortBy + "_" + sortDirection
                                : ""
                            }
                            onChange={(
                              _event: React.SyntheticEvent | null,
                              value: string | null
                            ) => {
                              if (!value) {
                                return;
                              }

                              if (value.length > 1) {
                                const [field, order] = value.split("_");
                                searchParams.set("sort_by", field);
                                searchParams.set("sort_direction", order);
                              } else {
                                searchParams.delete("sort_by");
                                searchParams.delete("sort_direction");
                              }
                              setSearchParams(searchParams);
                            }}
                          >
                            {sortOptions.map((opt) => {
                              if (opt.value.includes("price")) {
                                return;
                              }

                              return (
                                <Option
                                  key={opt.value}
                                  value={opt.value}
                                  color="primary"
                                  sx={{
                                    color:
                                      theme.colorSchemes.dark.palette.common
                                        .black,
                                  }}
                                >
                                  {t(`catalog.sortOptions.${opt.label}`)}
                                </Option>
                              );
                            })}
                          </Select>

                          {/* new view mode */}
                          <Stack
                            direction={"row"}
                            sx={{
                              borderWidth: "1px",
                              borderColor: "#9ca3af",
                              borderRadius: "0.5rem",
                              overflow: "hidden",
                              maxWidth: "78px",
                              display: "flex",
                              justifyContent: "flex-end",
                              alignItems: "center",
                            }}
                          >
                            <IconButton
                              onClick={() => {
                                searchParams.set("layout", "list");
                                setSearchParams(searchParams);
                              }}
                              variant="plain"
                              className={`h-[100%] rounded-none px-2  ${
                                layoutType == "list" ? "bg-primary-200" : ""
                              }`}
                            >
                              <ListBulletIcon className="w-6" />
                            </IconButton>
                            <span className="border-l border-gray-400 h-[100%]"></span>
                            <IconButton
                              onClick={() => {
                                searchParams.set("layout", "grid");
                                setSearchParams(searchParams);
                              }}
                              variant="plain"
                              className={`h-[100%]  rounded-none ${
                                layoutType == "grid" ? "bg-primary-200" : ""
                              }`}
                            >
                              <Squares2X2Icon className="w-6" />
                            </IconButton>
                          </Stack>
                        </Stack>
                      </Stack>
                    </Stack>

                    {/* product list */}
                    {/* <Box
                      ref={scrollableRef}
                      // onScroll={(e: any) => {
                      //   e.currentTarget.scrollTop = 0;
                      // }}
                      className={"hide-scrollbar"}
                      sx={{
                        m: 0,
                        p: 0,
                        // mb: "100px",
                        width: "100%",
                        height: "700px",
                        overflowY: "auto",
                        backgroundColor: "red",
                      }}
                    >
                      <InfiniteScroll
                        pageStart={0}
                        useWindow={false}
                        loadMore={(_page: number) => {
                          if (isLoading) {
                            return;
                          }

                          console.log("PAGING CURRENT", pagination);

                          fetchAllItems({
                            catalogId: catalogId ?? "",
                            paging: true,
                            limit: 10,
                            offset: 10 * pagination.current,
                            searchTerm: query,
                            inventoryType:
                              inventoryType !== "all" ? inventoryType : null,
                            isReset: false,
                            sortDirection: (["asc", "desc"].includes(
                              sortDirection
                            )
                              ? sortDirection
                              : "desc") as "asc" | "desc",
                            sortBy: sortBy ? sortBy : "updatedAt",
                          });
                        }}
                        hasMore={hasMore}
                        loader={
                          isLoading ? (
                            <div className="loader" key={0}>
                              Loading...
                            </div>
                          ) : (
                            <></>
                          )
                        }
                        // getScrollParent={() => scrollableRef.current}
                      >
                        {items.length > 0 ? (
                          <Box width={"100%"}>
                            {layoutType === "grid" && (
                              <Grid
                                container
                                columns={{
                                  xs: 12,
                                  lg: 60,
                                }} // 12/2=6, 60/5=12
                                spacing={{ xs: "8px", md: "24px" }}
                                sx={{
                                  px: 0,
                                  // alignItems: "stretch",
                                  // gap: { xs: "8px", md: "24px" },
                                }}
                              >
                                {items.map((i) => {
                                  return (
                                    <Grid
                                      key={i.id}
                                      xs={6}
                                      lg={isGuestMode ? 10 : 12}
                                      xl={10}
                                    >
                                      <ProductCardBase
                                        item={i}
                                        catalog={catalog}
                                        mode="supplier"
                                        handleUpdateCart={handleUpdateCart}
                                        handleUpdateOrder={handleUpdateOrder}
                                        setCartUpdated={setCartUpdated}
                                      />
                                    </Grid>

                                    // <div key={i.id}>
                                    //   <SupplierProductCard
                                    //     item={i}
                                    //     catalog={catalog}
                                    //     orderDataCallback={
                                    //       orderDataCallback
                                    //     }
                                    //     type="supplier"
                                    //     displayType={"grid"}
                                    //     onClick={() => {
                                    //       setSelectedItem({
                                    //         show: true,
                                    //         item: i,
                                    //       });
                                    //     }}
                                    //   />
                                    // </div>
                                  );
                                })}
                              </Grid>
                            )}
                            {layoutType === "list" && (
                              <List>
                                {items.map((i) => {
                                  return (
                                    <ProductCardBase
                                      key={i.id}
                                      item={i}
                                      catalog={catalog}
                                      displayType="list"
                                      mode="supplier"
                                      handleUpdateCart={handleUpdateCart}
                                      handleUpdateOrder={handleUpdateOrder}
                                      setCartUpdated={setCartUpdated}
                                    />
                                    // <div key={i.id}>
                                    //   <SupplierProductCard
                                    //     item={i}
                                    //     catalog={catalog}
                                    //     setCartUpdated={setCartUpdated}
                                    //     orderDataCallback={
                                    //       orderDataCallback
                                    //     }
                                    //     type="supplier"
                                    //     displayType={"list"}
                                    //     onClick={() => {
                                    //       setSelectedItem({
                                    //         show: true,
                                    //         item: i,
                                    //       });
                                    //     }}
                                    //   />
                                    // </div>
                                  );
                                })}
                              </List>
                            )}
                          </Box>
                        ) : (
                          <div className="flex w-full mt-10">
                            <p className="mx-auto my-auto text-base">
                              {t("catalog.noItemInCatalog")}
                            </p>
                          </div>
                        )}
                      </InfiniteScroll>
                    </Box> */}

                    {selectedItem && (
                      <ProductDetailsModal
                        show={selectedItem.show}
                        item={selectedItem.item}
                        catalog={catalog}
                        isDisabled
                        onClose={(item: CatalogInventoryItemType) => {
                          setSelectedItem({
                            show: false,
                            item: item,
                          });
                        }}
                        withAddToCart={isMobile}
                        items={items}
                        orderDataCallback={orderDataCallback}
                        setCartUpdated={setCartUpdated}
                      />
                    )}

                    <Snackbar
                      variant="solid"
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "center",
                      }}
                      open={showToast}
                      key={"asjfkangdlknakjrsh"}
                      sx={{
                        width: "40%",
                        maxWidth: 800,
                      }}
                    >
                      <Box width={"100%"}>
                        <Typography
                          textAlign={"center"}
                          textColor={"common.white"}
                          fontSize={14}
                        >
                          {t(
                            `catalog.${
                              orderId ? "itemAddedToOrder" : "itemAddedToCart"
                            }`
                          )}
                        </Typography>
                      </Box>
                    </Snackbar>
                  </>
                )}
              </Stack>
            }
            elseBlock={
              <IfElse
                condition={isGuestMode && catalog?.catalogType === "PRIVATE"}
                ifBlock={catalog ? <PINForm catalog={catalog} /> : <></>}
                elseBlock={
                  <IfElse
                    condition={
                      !!catalog?.periodEndDate?.isAfter?.(dayjs(), "day")
                    }
                    ifBlock={
                      <Stack
                        direction={"column"}
                        sx={{
                          width: "100%",
                          justifyContent: "center",
                          alignItems: "center",
                          textAlign: "center",
                        }}
                      >
                        <img
                          src={"/catalog-expired.png"}
                          alt="catalog-expired"
                          width={472}
                          height={422}
                          className={"mx-auto"}
                        />

                        <Stack direction={"column"} gap={"1rem"}>
                          <Typography fontWeight={700} fontSize={32}>
                            Catalogue Expired
                          </Typography>
                          <Typography
                            fontWeight={500}
                            fontSize={16}
                            width={"676px"}
                          >
                            This catalogue is no longer available as it has
                            expired. Please check for other available catalogues
                            or contact the seller for more details.
                          </Typography>
                          <Link to={"/client/suppliers"}>
                            <Button className="rounded-full">
                              Back to Supplier Catalogue
                            </Button>
                          </Link>
                        </Stack>
                      </Stack>
                    }
                    elseBlock={
                      <ListUnavailable message={t("catalog.noAccessCatalog")} />
                    }
                  />
                }
              />
            }
          />
        }
        elseBlock={
          <Box display={"flex"} width={"100%"}>
            <If condition={error?.response?.status === 404}>
              <Typography
                level="text-md-medium"
                sx={{ mx: "auto", my: "32px" }}
              >
                Catalog not found
              </Typography>
            </If>
            <If condition={error?.response?.status === 500}>
              <Typography
                level="text-md-medium"
                sx={{ mx: "auto", my: "32px" }}
              >
                Something went wrong
              </Typography>
            </If>
          </Box>
        }
      />

      {/* product list infinite scroll (moved to get full page parent) */}
      <If condition={!error && !!catalog}>
        <Box
          className={"hide-scrollbar"}
          sx={{
            width: "100%",
            height: {
              xs: "700px",
              lg: "900px",
              xl: "1250px",
            },
            "@media (min-width: 2500px)": {
              height: "1600px", // 3840x2160 (custom media query)
            },
            overflowY: "auto",
          }}
        >
          <InfiniteScroll
            initialLoad={false}
            pageStart={0}
            useWindow={false}
            loadMore={(_page: number) => {
              if (isItemLoading) {
                return;
              }

              console.log("PAGING CURRENT", pagination);

              fetchAllItems({
                catalogId: catalogId ?? "",
                paging: true,
                limit: 20,
                offset: 20 * pagination.current,
                searchTerm: query,
                inventoryType: inventoryType !== "all" ? inventoryType : null,
                isReset: false,
                sortDirection: (["asc", "desc"].includes(sortDirection)
                  ? sortDirection
                  : "desc") as "asc" | "desc",
                sortBy: sortBy ? sortBy : "updatedAt",
              });
            }}
            hasMore={hasMore}
            loader={
              isItemLoading ? (
                <div className="loader" key={0}>
                  Loading...
                </div>
              ) : (
                <></>
              )
            }
            // getScrollParent={() => scrollableRef.current}
          >
            {items.length > 0 ? (
              <Box width={"100%"}>
                {layoutType === "grid" && (
                  <Grid
                    container
                    columns={{
                      xs: 12,
                      lg: 60,
                    }} // 12/2=6, 60/5=12
                    spacing={{ xs: "8px", md: "24px" }}
                    sx={{
                      px: 0,
                      // alignItems: "stretch",
                      // gap: { xs: "8px", md: "24px" },
                    }}
                  >
                    {items.map((i) => {
                      return (
                        <Grid
                          key={i.id}
                          xs={6}
                          lg={isGuestMode ? 10 : 12}
                          xl={10}
                        >
                          <ProductCardBase
                            item={i}
                            catalog={catalog}
                            mode="supplier"
                            handleUpdateCart={handleUpdateCart}
                            handleUpdateOrder={handleUpdateOrder}
                            setCartUpdated={setCartUpdated}
                          />
                        </Grid>

                        // <div key={i.id}>
                        //   <SupplierProductCard
                        //     item={i}
                        //     catalog={catalog}
                        //     orderDataCallback={
                        //       orderDataCallback
                        //     }
                        //     type="supplier"
                        //     displayType={"grid"}
                        //     onClick={() => {
                        //       setSelectedItem({
                        //         show: true,
                        //         item: i,
                        //       });
                        //     }}
                        //   />
                        // </div>
                      );
                    })}
                  </Grid>
                )}
                {layoutType === "list" && (
                  <List>
                    {items.map((i) => {
                      return (
                        <ProductCardBase
                          key={i.id}
                          item={i}
                          catalog={catalog}
                          displayType="list"
                          mode="supplier"
                          handleUpdateCart={handleUpdateCart}
                          handleUpdateOrder={handleUpdateOrder}
                          setCartUpdated={setCartUpdated}
                        />
                        // <div key={i.id}>
                        //   <SupplierProductCard
                        //     item={i}
                        //     catalog={catalog}
                        //     setCartUpdated={setCartUpdated}
                        //     orderDataCallback={
                        //       orderDataCallback
                        //     }
                        //     type="supplier"
                        //     displayType={"list"}
                        //     onClick={() => {
                        //       setSelectedItem({
                        //         show: true,
                        //         item: i,
                        //       });
                        //     }}
                        //   />
                        // </div>
                      );
                    })}
                  </List>
                )}
              </Box>
            ) : (
              <div className="flex w-full mt-10">
                <p className="mx-auto my-auto text-base">
                  {t("catalog.noItemInCatalog")}
                </p>
              </div>
            )}
          </InfiniteScroll>
        </Box>
      </If>

      <LoadingModal isLoading={isLoading} />
    </Sheet>
  );
};

const PINForm = ({ catalog }: { catalog: CatalogType }) => {
  const { t } = useTranslation();

  const [PINInput, setPINInput] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const verifyPINMutation = useMutation({
    mutationFn: ({
      catalogId,
      businessId,
      accountId,
      pin,
    }: {
      catalogId: string;
      businessId: string | null;
      accountId: string | null;
      pin: string;
    }) =>
      SupplierService.verifyAccess({
        catalogId,
        businessId,
        accountId,
        pin,
      }),
    onSuccess: (data) => {
      console.log("RESPONSE", data);

      if (!data.pinStatus) {
        handleSaveCatalogAccess(catalog.id);

        window.location.reload();
      } else {
        setErrorMessage(
          t(`catalog.supplier.verifyAccess.errors.${data.pinStatus}`)
        );
      }
    },
    onError: (error) => {
      toast(showErrorToast(error), ErrorToastConfig);
    },
  });

  const onSubmit = () => {
    // console.log("DATA", PINInput);
    verifyPINMutation.mutate({
      catalogId: catalog.id,
      businessId: null,
      accountId: null,
      pin: PINInput,
    });
  };

  /**
   * take catalogId and add into catalog_access object
   * @param catalogId
   */
  const handleSaveCatalogAccess = (catalogId: string): void => {
    const existingData = localStorage.getItem("catalog_access");
    let catalogAccess: CatalogAccess[] = existingData
      ? JSON.parse(existingData)
      : [];

    const existingItem = catalogAccess.find(
      (item) => item.catalogId === catalogId
    );

    if (existingItem) {
      existingItem.date = new Date();
    } else {
      catalogAccess.push({ catalogId, date: new Date() });
    }

    localStorage.setItem("catalog_access", JSON.stringify(catalogAccess));
  };

  return (
    <Box
      sx={{
        position: "absolute",
        zIndex: 9999,
        backgroundColor: theme.palette.neutral[200],
        top: 0,
        left: 0,
        width: "100dvw",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        p: { xs: "16px", md: "40px" },
      }}
    >
      {/* <Box
        sx={{
          position: "relative",
          p: { xs: "16px", md: "40px" },
          // backgroundColor: theme.palette.neutral[200],
          backgroundColor: "red",
          height: "100dvh",
          overflow: "auto",
        }}
      > */}
      {/* linkz logo */}
      <Stack
        direction={"row"}
        sx={{
          width: "100%",
          justifyContent: { xs: "center", md: "flex-start" },
        }}
      >
        <Link to={"/"}>
          <img
            src={LogoImg}
            alt="linkz-logo"
            width={84.13}
            height={40}
            className={"mr-auto"}
          />
        </Link>
      </Stack>

      {/* form container */}
      <form>
        <Stack
          direction={"column"}
          sx={{
            gap: "24px",
            p: { xs: "24px", md: "40px" },
            borderRadius: "40px",
            backgroundColor: theme.palette.common.white,
            // mt: { xs: "120px", md: "200px" },
            mt: { xs: "120px" },
            alignItems: "center",
            width: { md: "500px" },
            height: { md: "428px" },
            mx: "auto",
          }}
        >
          <LockClosedIcon
            width={55}
            height={70}
            color={theme.palette.primary[500]}
          />

          {/* PIN prompt */}
          <Stack
            direction={"column"}
            sx={{
              gap: "12px",
              textAlign: "center",
              alignItems: "center",
            }}
          >
            <Typography level="text-xl2-bold">
              {t("catalog.supplier.verifyAccess.pinTitle")}
            </Typography>

            <Typography
              level="text-md-regular"
              textColor={theme.palette.text.secondary}
            >
              {t("catalog.supplier.verifyAccess.pinPrompt")}
            </Typography>
          </Stack>

          {/* PIN fields */}
          <Stack
            direction={"column"}
            sx={{
              width: "100%",
              gap: "16px",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <OTPInput
              inputType="tel"
              containerStyle={{
                gap: "8px",
                justifyContent: "center",
              }}
              value={PINInput}
              onChange={setPINInput}
              numInputs={6}
              skipDefaultStyles
              renderInput={(props, i) => (
                <Input
                  id={"pin-input-" + i}
                  size="lg"
                  sx={{
                    minWidth: { xs: "45px", md: "52px" },
                    maxWidth: { xs: "45px", md: "52px" },
                    minHeight: { xs: "56px", md: "64px" },
                    fontWeight: 600,
                    fontSize: "30px",
                    lineHeight: "38px",
                    px: "8px",
                  }}
                  slotProps={{
                    input: {
                      ...props,
                      sx: {
                        textAlign: "center",
                      },
                    },
                  }}
                />
              )}
            />

            {/* error message */}
            <Typography
              level="text-md-regular"
              textColor={theme.palette.danger[500]}
            >
              {errorMessage}
            </Typography>
          </Stack>

          {/* button */}
          <Button
            fullWidth
            variant="linkz-filled-primary-rounded"
            disabled={PINInput.length < 6}
            type="button"
            onClick={onSubmit}
          >
            {t("commons.continueBtn")}
          </Button>
        </Stack>
      </form>
      {/* </Box> */}
    </Box>
  );
};

export default ListSupplierItems;
